<script>
    import ShareIcon from "@propublica/pp-v5-shared/src/icons/share.svg";
    import MailIcon from "@propublica/pp-v5-shared/src/icons/mail.svg";
    import XIcon from "@propublica/pp-v5-shared/src/icons/social-x.svg";
    import FacebookIcon from "@propublica/pp-v5-shared/src/icons/social-facebook.svg";
    import WhatsAppIcon from "@propublica/pp-v5-shared/src/icons/social-whatsapp.svg";
    import ThreadsIcon from "@propublica/pp-v5-shared/src/icons/social-threads.svg";
    import CloseIcon from "@propublica/pp-v5-shared/src/icons/close.svg";

    import { Popover } from "bits-ui";
    import Button from "../Button";
    import CopyButton from "../CopyButton";

    // Prop fallbacks are for SSR, when window and document aren't available

    /** The URL to be shared. If you are instantiating ShareTools in SSR, then
     * you must specify this URL; otherwise, the ShareTools will not show up. If
     * you are only instantiating ShareTools in client JS, then it defaults to
     * the current page's URL, detected by JS (no need to specify this prop).
     * @type { string | null }
     */
    export let pageUrl =
        typeof window !== "undefined" ? window.location.href : null;

    /** The page title that will get autopopulated in the tweet dialog. If
     * you are instantiating ShareTools in SSR, we recommend that you fill
     * this; otherwise it defaults to an empty string. If you are only
     * instantiating ShareTools in client JS, then it defaults to the page's
     * `<title>` (no need to specify this prop).
     * @type {string}
     */
    export let pageTitle =
        typeof document !== "undefined" ? document.title : "";

    /**
     * Controls whether popover is open.
     * @type {boolean}
     */
    export let isPopoverOpen = false;

    /**
     * Props passed to {@link Button} used for main share button. Can be used
     * to override styles. Note that if you pass any props, all the default
     * styles are not included.
     * @type {import("svelte").ComponentProps<Button>}
     */
    export let buttonProps = {
        bgColor: "var(--color-neutral-10)",
        shape: "pill",
        textColor: "var(--color-neutral-70)",
        textSize: "var(--scale-2)",
    };

    function closePopover() {
        isPopoverOpen = false;
    }

    /**
     * Encoded version of {@link pageUrl} for use as a query param in another
     * URL. (If `pageUrl` is null, then this is an empty string.)
     */
    const encodedPageUrl = encodeURIComponent(pageUrl ?? "");

    /**
     * Props passed to {@link Button} used for each share link, including copy
     * @type {import("svelte").ComponentProps<Button>}
     */
    const commonButtonProps = {
        bgColor: "var(--color-bg)",
        textColor: "var(--color-text-body)",
        border: "1px solid var(--color-neutral-10)",
        stretchTapTarget: true,
    };

    /**
     * @typedef {object} ShareConfig Configures a share link for a service
     * @property {string} name Name of the service, for the share link text
     * @property {string} href URL that lets the user share the page to the
     *     service; it should interpolate {@link encodedPageURL}
     * @property {import("svelte").ComponentType} icon The icon for the service
     */

    /**
     * Each entry configures one share option. Order matters.
     * @type {ShareConfig[]}
     */
    const shareConfigs = [
        {
            name: "Email",
            href: `mailto:?body=${encodedPageUrl}`,
            icon: MailIcon,
        },
        {
            name: "X (Twitter)",
            href: `https://x.com/intent/tweet?url=${encodedPageUrl}&text=${encodeURIComponent(
                pageTitle
            )}`,
            icon: XIcon,
        },
        {
            name: "Facebook",
            href: `https://www.facebook.com/sharer/sharer.php?u=${encodedPageUrl}`,
            icon: FacebookIcon,
        },
        {
            name: "WhatsApp",
            href: `https://api.whatsapp.com/send?text=${encodedPageUrl}`,
            icon: WhatsAppIcon,
        },
        {
            name: "Threads",
            href: `https://www.threads.net/intent/post?text=${encodedPageUrl}`,
            icon: ThreadsIcon,
        },
    ];
</script>

{#if pageUrl !== null}
    <div class="share-tools" data-pp-location="share tools">
        <Popover.Root bind:open={isPopoverOpen} openFocus>
            <Popover.Trigger asChild let:builder>
                <Button {...buttonProps} builders={[builder]}>
                    <ShareIcon slot="iconBefore" />
                    Share
                </Button>
            </Popover.Trigger>
            <Popover.Content class="share-tools__popover">
                <div class="share-tools__popover-title hide-above-lg">
                    Share
                    <Button
                        on:click={closePopover}
                        bgColor="transparent"
                        textColor="var(--color-text-hed)"
                        iconOnly={true}
                        iconSize="80%"
                        shape="circle"
                    >
                        <CloseIcon slot="iconBefore" />
                        Close
                    </Button>
                </div>
                <ul class="share-tools__list">
                    <li
                        class="share-tools__copy-container stretched-link-contain"
                    >
                        <span class="share-tools_copy-text">{pageUrl}</span>
                        <CopyButton
                            textToCopy={pageUrl}
                            buttonProps={{
                                ...commonButtonProps,
                                textSize: "var(--scale-2)",
                                shape: "pill",
                            }}
                        />
                    </li>
                    {#each shareConfigs as share}
                        <li class="share-tools__item stretched-link-contain">
                            <Button
                                elementType="a"
                                href={share.href}
                                {...commonButtonProps}
                                iconOnly={true}
                                shape="circle"
                            >
                                <svelte:component
                                    this={share.icon}
                                    slot="iconBefore"
                                />
                                {share.name}
                            </Button>
                            {share.name}
                        </li>
                    {/each}
                </ul>
            </Popover.Content>
        </Popover.Root>
    </div>
{/if}

<style>:global(.share-tools__popover) {
  background-color: var(--color-bg);
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
}
@media screen and (max-width: 60em) {
  :global(.share-tools__popover) {
    position: fixed !important;
    top: unset !important;
    bottom: 0 !important;
    left: 0 !important;
    border-radius: 4px 4px 0 0;
    padding: var(--page-margin, var(--spacing0));
    width: 100vw !important;
  }
}
@media screen and (min-width: 60em) {
  :global(.share-tools__popover) {
    border-radius: 4px;
    padding: var(--spacing-1);
    width: 12rem;
  }
}

@media screen and (max-width: 60em) {
  .share-tools__item :global(.button) {
    --button-size: var(--scale5) !important;
    --icon-size: 60% !important;
  }
}

.share-tools__popover-title {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-family: var(--fonts-sans);
  font-size: var(--scale0);
  font-weight: 700;
  margin-bottom: var(--spacing0);
  padding: 4px;
}

.share-tools__list {
  font-family: var(--fonts-sans);
}
.share-tools__list li {
  list-style: none;
}

@media screen and (max-width: 60em) {
  .share-tools__copy-container :global(.button) {
    --button-font-size: var(--scale-1) !important;
    --button-size: var(--scale4) !important;
  }
}

:global(html.force-light-mode) .share-tools__copy-container:hover {
  background-color: var(--gray-3);
}
@media (prefers-color-scheme: light) {
  :global(html:not(.force-dark-mode)) .share-tools__copy-container:hover {
    background-color: var(--gray-3);
  }
}
:global(html.force-dark-mode) .share-tools__copy-container:hover {
  background-color: var(--color-neutral-10);
}
@media screen and (prefers-color-scheme: dark) {
  :global(html:not(.force-light-mode)) .share-tools__copy-container:hover {
    background-color: var(--color-neutral-10);
  }
}

.share-tools_copy-text {
  color: var(--color-text-meta);
  font-size: var(--scale-3);
  line-height: 1;
  overflow: hidden;
  text-wrap: nowrap;
  margin-left: var(--spacing-2);
}
@media screen and (max-width: 60em) {
  .share-tools_copy-text {
    font-size: var(--scale-2);
  }
}

.share-tools__copy-container {
  display: flex;
  align-items: center;
  border-radius: 400px;
  border: 1px solid var(--color-neutral-10);
  justify-content: space-between;
  padding: 4px 4px 4px 8px;
  margin: 4px 4px var(--spacing-1) 4px;
  transition: background-color linear 0.1s;
}
@media screen and (max-width: 60em) {
  .share-tools__copy-container {
    padding: 2px 2px 2px 8px;
  }
}

.share-tools__item {
  display: flex;
  align-items: center;
  font-size: var(--scale-2);
  color: var(--color-text-body);
  gap: var(--spacing-1);
  font-weight: 700;
  padding: 4px;
  border-radius: 999px;
  transition: background-color linear 0.1s;
}
:global(html.force-light-mode) .share-tools__item:hover {
  background-color: var(--gray-3);
}
@media (prefers-color-scheme: light) {
  :global(html:not(.force-dark-mode)) .share-tools__item:hover {
    background-color: var(--gray-3);
  }
}
:global(html.force-dark-mode) .share-tools__item:hover {
  background-color: var(--color-neutral-10);
}
@media screen and (prefers-color-scheme: dark) {
  :global(html:not(.force-light-mode)) .share-tools__item:hover {
    background-color: var(--color-neutral-10);
  }
}
@media screen and (max-width: 60em) {
  .share-tools__item {
    font-size: var(--scale-1);
    gap: var(--spacing0);
    padding: 6px 4px;
  }
}</style>
